import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { computed, ref, watch } from "@vue/composition-api";

export default function useUsersList() {
    const refTable = ref(null);
    const tableColumns = [
        {
            key: "name",
            label: i18n.t("settings.users.name.title"),
            sortable: true,
        },
        { key: "email", label: i18n.t("settings.users.email"), sortable: true },
        {
            key: "lastLogin",
            label: i18n.t("settings.users.last-login"),
            sortable: true,
            formatter: (value) => {
                return value ? new Date(value).toLocaleString() : ""; // Display the time in a more readable way
            },
        },
        {
            key: "role",
            label: i18n.t("settings.users.role"),
            sortable: true,
            formatter: (value) => i18n.t(`roles.${value}`),
        },
        {
            key: "teams",
            label: i18n.t("settings.users.teams"),
            sortable: false,
            formatter: (value) => value.map((value) => value.name).toString(),
        },
        {
            key: "active",
            label: i18n.t("settings.users.active"),
            sortable: true,
        },
        { key: "actions", label: i18n.t("settings.users.actions") },
    ];
    const perPage = ref(10);
    const total = ref(0);
    const currentPage = ref(1);
    const perPageOptions = [10, 25, 50, 100];
    const searchQuery = ref("");
    const sortBy = ref("name");
    const isSortDirDesc = ref(false);
    const isLoading = ref(true);

    const dataMeta = computed(() => {
        const localItemsCount = refTable.value ? refTable.value.localItems.length : 0;

        return {
            from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
            to: perPage.value * (currentPage.value - 1) + localItemsCount,
            of: total.value,
        };
    });

    const refetchData = () => {
        refTable.value.refresh();
    };

    watch([currentPage, perPage, searchQuery], () => {
        if (searchQuery.value.length > 0 && searchQuery.value.length < 2) {
            return;
        }

        refetchData();
    });

    const read = (ctx, callback) => {
        axios
            .get("users", {
                params: {
                    fields: "name,email,lastLogin,role,active,pictureUrl",
                    s: {
                        $and: [
                            {
                                $or: [
                                    { name: { $cont: searchQuery.value } },
                                    { email: { $cont: searchQuery.value } },
                                    { lastLogin: { $cont: searchQuery.value } },
                                    { role: { $cont: searchQuery.value } },
                                    { active: { $cont: searchQuery.value } },
                                ],
                            },
                            { role: { $ne: "API" } }, // Don't display API user in the frontend
                            { role: { $ne: "SAYSIMPLE" } }, // Don't display SAYSIMPLE user in the frontend
                        ],
                    },
                    join: "teams",
                    limit: perPage.value,
                    page: currentPage.value,
                    sort: `${sortBy.value},${isSortDirDesc.value === true ? "DESC" : "ASC"}`,
                },
            })
            .then((response) => {
                callback(response.data.data);
                total.value = response.data.total;
            })
            .finally(() => (isLoading.value = false));
    };

    return {
        read,
        refetchData,
        tableColumns,
        perPage,
        currentPage,
        total,
        dataMeta,
        perPageOptions,
        searchQuery,
        sortBy,
        isSortDirDesc,
        refTable,
        isLoading,
    };
}
